import React, { useRef, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { 
  Warning as WarningIcon 
} from '@mui/icons-material';
import mapboxgl from 'mapbox-gl';
import { UserInfoResponse, GeoBarrier, PastTouchPoint } from '../../models/userInfo';
import { MAPBOX_ACCESS_TOKEN } from '../../config';

// Set your Mapbox token here - in a real app, this would be from env variables
// mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN; // Replace with your actual token

// Interface for the props
interface UserInfoMapViewProps {
  userInfo: UserInfoResponse | null;
  loading: boolean;
  error: string | null;
  visibleTouchPoints: string[];
  showGeoBarriers: boolean;
}

/**
 * A component that displays a map with geo barriers, touch points, and source location
 */
const UserInfoMapView: React.FC<UserInfoMapViewProps> = ({
  userInfo,
  loading,
  error,
  visibleTouchPoints,
  showGeoBarriers
}) => {
  // Reference to the map container
  const mapContainerRef = useRef<HTMLDivElement>(null);
  
  // Reference to the map instance
  const mapRef = useRef<mapboxgl.Map | null>(null);
  
  // State for map loaded status
  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  
  // Initialize the map when the component mounts
  useEffect(() => {
    if (!mapContainerRef.current) return;
    
    // Create a new map instance
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [72.5, 23.0], // Default center (will be updated later)
      zoom: 11,
    });
    
    // Save map instance to ref
    mapRef.current = map;
    
    // Handle map load event
    map.on('load', () => {
      setMapLoaded(true);
      
      // Add sources and layers for geo barriers and touch points
      map.addSource('geo-barriers', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        }
      });
      
      map.addSource('touch-points', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        }
      });
      
      map.addSource('source-location', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        }
      });
      
      // Add layer for geo barriers
      map.addLayer({
        id: 'geo-barriers-fill',
        type: 'fill',
        source: 'geo-barriers',
        layout: {},
        paint: {
          'fill-color': '#f03b20',
          'fill-opacity': 0.5,
        }
      });
      
      map.addLayer({
        id: 'geo-barriers-line',
        type: 'line',
        source: 'geo-barriers',
        layout: {},
        paint: {
          'line-color': '#f03b20',
          'line-width': 2,
        }
      });
      
      // Add layer for touch points
      map.addLayer({
        id: 'touch-points',
        type: 'circle',
        source: 'touch-points',
        paint: {
          'circle-radius': 6,
          'circle-color': '#3bb2d0',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        }
      });
      
      // Add layer for source location
      map.addLayer({
        id: 'source-location',
        type: 'circle',
        source: 'source-location',
        paint: {
          'circle-radius': 10,
          'circle-color': '#ff9900',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        }
      });
      
      // Add popups on hover
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false
      });
      
      // Show popup for geo barriers
      map.on('mouseenter', 'geo-barriers-fill', (e) => {
        if (!e.features || e.features.length === 0) return;
        
        map.getCanvas().style.cursor = 'pointer';
        
        const feature = e.features[0];
        const coordinates = e.lngLat;
        const properties = feature.properties;
        
        const html = `
          <strong>${properties?.name || 'Geo Barrier'}</strong><br />
          Allowed Vehicles: ${properties?.allowedVehTypes || 'None'}<br />
        `;
        
        popup.setLngLat(coordinates).setHTML(html).addTo(map);
      });
      
      map.on('mouseleave', 'geo-barriers-fill', () => {
        map.getCanvas().style.cursor = '';
        popup.remove();
      });
      
      // Show popup for touch points
      map.on('mouseenter', 'touch-points', (e) => {
        if (!e.features || e.features.length === 0) return;
        
        map.getCanvas().style.cursor = 'pointer';
        
        const feature = e.features[0];
        const coordinates = feature.geometry.type === 'Point' 
          ? (feature.geometry.coordinates as [number, number]) 
          : e.lngLat;
        const properties = feature.properties;
        
        const html = `
          <strong>${properties?.tpName || 'Touch Point'}</strong><br />
          ${properties?.tpAddress || ''}<br />
          ZIP: ${properties?.tpZipCode || ''}
        `;
        
        popup.setLngLat(coordinates).setHTML(html).addTo(map);
      });
      
      map.on('mouseleave', 'touch-points', () => {
        map.getCanvas().style.cursor = '';
        popup.remove();
      });
      
      // Show popup for source location
      map.on('mouseenter', 'source-location', (e) => {
        if (!e.features || e.features.length === 0) return;
        
        map.getCanvas().style.cursor = 'pointer';
        
        const feature = e.features[0];
        const coordinates = feature.geometry.type === 'Point' 
          ? (feature.geometry.coordinates as [number, number]) 
          : e.lngLat;
        
        const html = `
          <strong>Hub Location</strong><br />
          Depot marker<br />
        `;
        
        popup.setLngLat(coordinates).setHTML(html).addTo(map);
      });
      
      map.on('mouseleave', 'source-location', () => {
        map.getCanvas().style.cursor = '';
        popup.remove();
      });
    });
    
    // Clean up the map instance when the component unmounts
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);
  
  // Update map data when userInfo, visibleTouchPoints, or showGeoBarriers change
  useEffect(() => {
    if (!mapRef.current || !mapLoaded || !userInfo) return;
    
    const map = mapRef.current;
    
    // Update geo barriers source
    if (showGeoBarriers && userInfo.config.geoBarriers.length > 0) {
      const geoBarriersSource = map.getSource('geo-barriers') as mapboxgl.GeoJSONSource;
      
      if (geoBarriersSource) {
        geoBarriersSource.setData({
          type: 'FeatureCollection',
          features: userInfo.config.geoBarriers.map(barrier => convertGeoBarrierToFeature(barrier)),
        });
      }
    } else {
      // Clear the source if geo barriers should not be shown
      const geoBarriersSource = map.getSource('geo-barriers') as mapboxgl.GeoJSONSource;
      
      if (geoBarriersSource) {
        geoBarriersSource.setData({
          type: 'FeatureCollection',
          features: [],
        });
      }
    }
    
    // Update touch points source
    const touchPointsSource = map.getSource('touch-points') as mapboxgl.GeoJSONSource;
    
    if (touchPointsSource) {
      touchPointsSource.setData({
        type: 'FeatureCollection',
        features: userInfo.pastTps
          .filter(tp => visibleTouchPoints.includes(tp.tpId))
          .map(tp => convertTouchPointToFeature(tp)),
      });
    }
    
    // Update source location
    const sourceLocationSource = map.getSource('source-location') as mapboxgl.GeoJSONSource;
    
    if (sourceLocationSource && userInfo.config.sourceLngLat) {
      sourceLocationSource.setData({
        type: 'FeatureCollection',
        features: [{
          type: "Feature" as const,
          geometry: {
            type: "Point" as const,
            coordinates: userInfo.config.sourceLngLat,
          },
          properties: {
            hubId: userInfo.userId,
          },
        }],
      });
    }
    
    // Fit map bounds to include all visible features
    const bounds = new mapboxgl.LngLatBounds();
    
    // Add source location to bounds
    if (userInfo.config.sourceLngLat) {
      bounds.extend(userInfo.config.sourceLngLat as [number, number]);
    }
    
    // Add geo barriers to bounds
    if (showGeoBarriers) {
      userInfo.config.geoBarriers.forEach(barrier => {
        barrier.geoBoundary.forEach(point => {
          bounds.extend(point as [number, number]);
        });
      });
    }
    
    // Add touchpoints to bounds
    userInfo.pastTps
      .filter(tp => visibleTouchPoints.includes(tp.tpId))
      .forEach(tp => {
        bounds.extend(tp.geoLoc as [number, number]);
      });
    
    // If we have bounds to fit, do it with padding
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, {
        padding: 50,
        maxZoom: 15,
      });
    }
  }, [userInfo, visibleTouchPoints, showGeoBarriers, mapLoaded]);
  
  // Helper function to convert a geo barrier to a GeoJSON feature
  const convertGeoBarrierToFeature = (barrier: GeoBarrier) => {
    return {
      type: "Feature" as const,
      geometry: {
        type: "Polygon" as const,
        coordinates: [barrier.geoBoundary],
      },
      properties: {
        id: barrier.id,
        name: barrier.name,
        allowedVehTypes: barrier.allowedVehTypes.join(', '),
      },
    };
  };
  
  // Helper function to convert a touch point to a GeoJSON feature
  const convertTouchPointToFeature = (tp: PastTouchPoint) => {
    return {
      type: "Feature" as const,
      geometry: {
        type: "Point" as const,
        coordinates: tp.geoLoc,
      },
      properties: {
        tpId: tp.tpId,
        tpName: tp.tpName,
        tpAddress: tp.tpAddress,
        tpZipCode: tp.tpZipCode,
      },
    };
  };
  
  // Show loading indicator
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Show error message
  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
        <WarningIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          Error
        </Typography>
        <Typography variant="body1">{error}</Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Map container */}
      <Box 
        ref={mapContainerRef} 
        sx={{ 
          width: '100%', 
          height: '100%',
          borderRadius: 1,
          overflow: 'hidden'
        }} 
      />
      
      {/* Map legend */}
      <Box 
        sx={{ 
          position: 'absolute', 
          bottom: 16, 
          right: 16, 
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: 1,
          padding: 2,
          boxShadow: 2,
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Map Legend
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box 
            sx={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%', 
              backgroundColor: '#ff9900',
              border: '2px solid #ffffff',
              mr: 1 
            }} 
          />
          <Typography variant="body2">Hub Location</Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box 
            sx={{ 
              width: 16, 
              height: 16, 
              borderRadius: '50%', 
              backgroundColor: '#3bb2d0',
              border: '2px solid #ffffff',
              mr: 1 
            }} 
          />
          <Typography variant="body2">Touch Points</Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box 
            sx={{ 
              width: 16, 
              height: 16, 
              backgroundColor: '#f03b20',
              opacity: 0.5,
              border: '1px solid #f03b20',
              mr: 1 
            }} 
          />
          <Typography variant="body2">Geo Barriers</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default UserInfoMapView; 
